import { getCoreUtils } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppSignUpApi } from "./AppSignUpApi";

// AppChatArchiveRules - THE WIRE of one chat's settings: where a read and a write go, what a write carries, which values
// the service admits, and each setting's name. Pure (no DOM, no transport).
// ⚠ WHAT A SETTING'S VALUE IS, AND ITS WORDS, live in `AppChatState` (the service's answer) and `AppChatSettingsRules`
// (the "Chat settings" dialog). The per-menu-item labels this file used to hold - with their "status unknown" fallbacks -
// were REMOVED on 2026-10-13 (owner: "No property should ever be displayed as un-known"); do not bring them back.
// A different product would change the routes and the admitted values.

export type AppChatSettingValue = boolean | number;

/** The current view's identity; no key is put in a URL or retained across contact views. */
export class AppChatArchiveWho {
    public ServiceUrl: string = "";
    public Number: string = "";
    public Key: string = "";
    public ChatId: string = "";
    public Setting: string = "archive";
}

export class AppChatArchiveRules {
    public static readonly Route: string = "/chat/archive";
    public static readonly ReadTimeoutMs: number = 10000;
    public static readonly SetTimeoutMs: number = 40000;

    public static Url = (Who: AppChatArchiveWho, Read: boolean, Combined: boolean = false): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Route: string = AppChatArchiveRules.Route;
            if (Who.Setting !== "archive" || Combined === true) { Route = "/chat/settings"; }
            const Base: string = AppSignUpApi.UrlFor(Who.ServiceUrl, Route);
            if (Base.length === 0 || Read === false) { return Base; }
            return Base + "?number=" + encodeURIComponent(Who.Number) + "&chatId=" + encodeURIComponent(Who.ChatId);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppChatArchiveRules.Url"); return ""; }
    };

    public static Body = (Who: AppChatArchiveWho, Archived: AppChatSettingValue, Combined: boolean = false): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Who.Setting !== "archive" || Combined === true) {
                return JSON.stringify({ number: Who.Number, key: Who.Key, chatId: Who.ChatId, action: "set", setting: Who.Setting, value: Archived });
            }
            return JSON.stringify({ number: Who.Number, key: Who.Key, chatId: Who.ChatId, action: "set", archived: Archived });
        }
        catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppChatArchiveRules.Body"); return ""; }
    };

    /** `Desired` true = a value the owner may ASK for (the service's own list); false = a value the service may answer. */
    public static ValueOk = (Setting: string, Value: unknown, Desired: boolean): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (Setting === "archive" || Setting === "star" || Setting === "pin" || Setting === "read" || Setting === "block") { return typeof Value === "boolean"; }
            // ⭐ An action (clear, delete) is asked with `true` only; the service never answers it as a value.
            if (Setting === "clear" || Setting === "delete") { return Value === true || Desired === false && typeof Value === "boolean"; }
            if (typeof Value !== "number" || Number.isSafeInteger(Value) === false) { return false; }
            if (Setting === "disappearing") { return [0, 86400, 604800, 7776000].indexOf(Value) >= 0; }
            if (Setting !== "mute") { return false; }
            if (Desired === true) { return [-1, 0, 28800, 604800].indexOf(Value) >= 0; }
            return Value === -1 || Value >= 0;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppChatArchiveRules.ValueOk"); return false; }
    };

    public static Name = (Setting: string): string => {
        if (getCoreUtils().KErrorMode === true) { return "Setting"; }
        try {
            if (Setting === "mute") { return "Notifications"; }
            if (Setting === "disappearing") { return "Disappearing messages"; }
            if (Setting === "star") { return "Star"; }
            if (Setting === "pin") { return "Pin chat"; }
            if (Setting === "read") { return "Read"; }
            if (Setting === "block") { return "Block contact"; }
            if (Setting === "clear") { return "Clear chat on your phone"; }
            if (Setting === "delete") { return "Delete chat on your phone"; }
            return "Archive";
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppChatArchiveRules.Name"); return "Setting"; }
    };
}
